'use client';

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { 
  CheckCircle, 
  XCircle, 
  AlertCircle, 
  Info,
  X
} from 'lucide-react';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  description?: string;
  duration?: number;
  link?: {
    url: string;
    text: string;
  };
}

interface ToastContextType {
  showToast: (type: ToastType, title: string, description?: string, duration?: number, link?: { url: string; text: string }) => void;
  success: (title: string, description?: string, link?: { url: string; text: string }) => void;
  error: (title: string, description?: string) => void;
  warning: (title: string, description?: string) => void;
  info: (title: string, description?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  
  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter(toast => toast.id !== id));
  }, []);
  
  const showToast = useCallback((
    type: ToastType,
    title: string,
    description?: string,
    duration: number = 4000,
    link?: { url: string; text: string }
  ) => {
    const id = Math.random().toString(36).substring(7);
    const newToast: Toast = {
      id,
      type,
      title,
      description,
      duration,
      link
    };

    setToasts((prev) => [...prev, newToast]);

    if (duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, duration);
    }
  }, [removeToast]);

  const success = useCallback((title: string, description?: string, link?: { url: string; text: string }) => {
    showToast('success', title, description, 4000, link);
  }, [showToast]);
  
  const error = useCallback((title: string, description?: string) => {
    showToast('error', title, description, 6000); // Errors stay longer
  }, [showToast]);
  
  const warning = useCallback((title: string, description?: string) => {
    showToast('warning', title, description);
  }, [showToast]);
  
  const info = useCallback((title: string, description?: string) => {
    showToast('info', title, description);
  }, [showToast]);
  
  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-600" />;
      case 'warning':
        return <AlertCircle className="h-5 w-5 text-yellow-600" />;
      case 'info':
        return <Info className="h-5 w-5 text-blue-600" />;
    }
  };
  
  const getStyles = (type: ToastType) => {
    switch (type) {
      case 'success':
        return 'bg-green-50 border-green-200';
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warning':
        return 'bg-yellow-50 border-yellow-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
    }
  };
  
  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info }}>
      {children}
      
      {/* Toast Container */}
      <div 
        style={{ 
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          zIndex: 99999,
          pointerEvents: 'none'
        }}
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            style={{
              pointerEvents: 'auto',
              maxWidth: '350px',
              width: '100%',
              marginBottom: '10px'
            }}
            className={`
              ${getStyles(toast.type)}
              bg-white
              border-2
              rounded-lg 
              shadow-lg 
              p-4 
              toast-slide-in
              transition-all
              duration-300
              hover:shadow-xl
            `}
          >
            <div className="flex items-start gap-3">
              {getIcon(toast.type)}
              <div className="flex-1">
                <p className="font-medium text-gray-900">
                  {toast.title}
                </p>
                {toast.description && (
                  <p className="mt-1 text-sm text-gray-600">
                    {toast.description}
                  </p>
                )}
                {toast.link && (
                  <a
                    href={toast.link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-2 inline-block text-sm font-medium text-blue-600 hover:text-blue-700 hover:underline"
                  >
                    {toast.link.text} →
                  </a>
                )}
              </div>
              <button
                onClick={() => removeToast(toast.id)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}